// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-control/ExecutivePosturePanel.jsx

import React, { useEffect, useRef, useState } from "react";
import { Button } from "../../design-system/components";
import MissionControlMetric from "./MissionControlMetric";
import MissionControlStatus from "./MissionControlStatus";

function scoreTone(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) return "neutral";
  if (value >= 90) return "success";
  if (value >= 75) return "warning";
  return "danger";
}

function riskLabel(tone, fallback) {
  if (tone === "low" || tone === "ok") return "Low Risk";
  if (tone === "medium" || tone === "warn") return "Medium Risk";
  if (tone === "high" || tone === "danger") return "High Risk";
  return fallback || "Posture unavailable";
}


export default function ExecutivePosturePanel({
  metrics,
  risk,
  formatSignedNumber,
  formatRelativeTime,
  formatTimestamp,
  refreshing = false,
  refreshMessage = "",
  refreshError = "",
  onRefresh,
  onOpenScoreDetails,
  onOpenTopRisks,
  onOpenAcceptedRisk,
  onOpenClosedRecords,
  onOpenAlerts,
  onOpenStaleControls,
  frameworks = [],
  selectedFramework = null,
  onFrameworkChange,
}) {
  const [frameworkMenuOpen, setFrameworkMenuOpen] = useState(false);
  const frameworkMenuRef = useRef(null);
  const authorizedFrameworks = Array.isArray(frameworks) ? frameworks : [];
  const activeFramework = authorizedFrameworks.includes(selectedFramework) ? selectedFramework : null;
  const operationalScore = typeof metrics?.operationalScore === "number" && Number.isFinite(metrics.operationalScore) ? metrics.operationalScore : null;
  const rawScore = typeof metrics?.rawScore === "number" && Number.isFinite(metrics.rawScore) ? metrics.rawScore : null;
  const ringDegrees = Math.max(0, Math.min(360, (operationalScore ?? 0) * 3.6));

  useEffect(() => {
    if (!frameworkMenuOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (frameworkMenuRef.current && !frameworkMenuRef.current.contains(event.target)) setFrameworkMenuOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setFrameworkMenuOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [frameworkMenuOpen]);

  const selectFramework = (framework) => {
    if (!authorizedFrameworks.includes(framework)) return;
    setFrameworkMenuOpen(false);
    if (framework !== selectedFramework && typeof onFrameworkChange === "function") onFrameworkChange(framework);
  };

  // The donut represents the authoritative operational score only.
  // The four supporting metrics below are not fabricated compliance-status segments.
  const supportingMetrics = [
    { label: "Raw score", value: rawScore === null ? "Not assessed" : `${rawScore.toFixed(1)}%`, tone: "bright" },
    { label: "Active findings", value: metrics?.activeFindings ?? 0, tone: "mid" },
    { label: "Stale controls", value: metrics?.staleCount ?? 0, tone: "pale" },
    { label: "Accepted risk", value: metrics?.acceptedRiskFindings ?? 0, tone: "soft" },
  ];

  return (
    <section className="mission-control-section executive-posture-panel compliance-progress-v2" aria-label="Compliance Progress">
      <header className="compliance-progress-v2__header">
        <h2>Compliance Progress</h2>
        <div className="compliance-progress-v2__selector" ref={frameworkMenuRef}>
          <button
            type="button"
            className="compliance-progress-v2__framework-button"
            aria-haspopup="listbox"
            aria-expanded={frameworkMenuOpen}
            aria-label="Choose organization framework"
            disabled={!activeFramework}
            onClick={() => setFrameworkMenuOpen((open) => !open)}
          >
            <span>{activeFramework || "Framework unavailable"}</span>
            <span aria-hidden="true" className="compliance-progress-v2__chevron">⌄</span>
          </button>
          {frameworkMenuOpen && activeFramework && (
            <div role="listbox" aria-label="Organization frameworks" className="compliance-progress-v2__framework-menu">
              {authorizedFrameworks.map((framework) => (
                <button
                  type="button"
                  role="option"
                  aria-selected={framework === activeFramework}
                  className="compliance-progress-v2__framework-option"
                  key={framework}
                  onClick={() => selectFramework(framework)}
                >{framework}</button>
              ))}
            </div>
          )}
        </div>
      </header>

      {refreshMessage || refreshError ? (
        <div className={`compliance-progress-v2__notice${refreshError ? " compliance-progress-v2__notice--error" : ""}`} role={refreshError ? "alert" : "status"}>
          {refreshError || refreshMessage}
        </div>
      ) : null}

      <div className="compliance-progress-v2__body">
        <div className="compliance-progress-v2__wheel-area">
          <div className="compliance-progress-v2__wheel" style={{ "--compliance-progress-degrees": `${ringDegrees}deg` }}>
            <div className="compliance-progress-v2__wheel-center">
              <strong>{operationalScore === null ? "—" : `${operationalScore.toFixed(1)}%`}</strong>
              <span>Operational score</span>
            </div>
          </div>
        </div>
        <div className="compliance-progress-v2__breakdown" aria-label="Current operational posture metrics">
          {supportingMetrics.map((item) => (
            <div className="compliance-progress-v2__breakdown-row" key={item.label}>
              <span className={`compliance-progress-v2__dot compliance-progress-v2__dot--${item.tone}`} aria-hidden="true" />
              <span className="compliance-progress-v2__breakdown-name">{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          ))}
          <div className="compliance-progress-v2__risk-line">
            <MissionControlStatus tone={risk?.tone}>{riskLabel(risk?.tone, risk?.label)}</MissionControlStatus>
          </div>
        </div>
      </div>

      <footer className="compliance-progress-v2__footer">
        <div className="compliance-progress-v2__freshness">
          <span>Data as of {formatRelativeTime(metrics?.dataAsOf)}</span>
          <small>{formatTimestamp(metrics?.dataAsOf)}</small>
        </div>
        <div className="compliance-progress-v2__actions">
          <Button variant="secondary" size="small" onClick={onRefresh} disabled={refreshing}>
            {refreshing ? "Refreshing…" : "Refresh view"}
          </Button>
          <Button variant="secondary" size="small" onClick={onOpenScoreDetails}>Score details</Button>
        </div>
      </footer>
    </section>
  );
}

// The original eight KPI tiles, moved as a presentation-only strip.
// Each tile retains its existing authoritative data and click handler.
export function ExecutivePostureMetrics({
  metrics,
  formatSignedNumber,
  onOpenScoreDetails,
  onOpenTopRisks,
  onOpenAcceptedRisk,
  onOpenClosedRecords,
  onOpenAlerts,
  onOpenStaleControls,
}) {
  const operationalScore = typeof metrics?.operationalScore === "number" && Number.isFinite(metrics.operationalScore) ? metrics.operationalScore : null;
  const rawScore = typeof metrics?.rawScore === "number" && Number.isFinite(metrics.rawScore) ? metrics.rawScore : null;
  const operationalTone = scoreTone(operationalScore);
  return (
        <div className="executive-posture-panel__metrics-grid">
          <MissionControlMetric
            label="Raw score"
            value={rawScore === null ? "Not assessed" : `${rawScore.toFixed(1)}%`}
            supportingText="All measured findings included"
            tone={scoreTone(rawScore)}
            onClick={onOpenScoreDetails}
          />
          <MissionControlMetric
            label="Operational score"
            value={operationalScore === null ? "Not assessed" : `${operationalScore.toFixed(1)}%`}
            supportingText={`${metrics?.acceptedRiskFindings || 0} accepted-risk finding${metrics?.acceptedRiskFindings === 1 ? "" : "s"} separated`}
            tone={operationalTone}
            onClick={onOpenScoreDetails}
          />
          <MissionControlMetric
            label="Operational delta"
            value={formatSignedNumber(metrics?.operationalScoreDelta)}
            supportingText="Compared with prior operational score"
            tone={
              metrics?.operationalScoreDelta > 0
                ? "success"
                : metrics?.operationalScoreDelta < 0
                ? "danger"
                : "neutral"
            }
            onClick={onOpenScoreDetails}
          />
          <MissionControlMetric
            label="Active findings"
            value={metrics?.activeFindings || 0}
            supportingText="Prioritized for review"
            tone={metrics?.activeFindings > 0 ? "danger" : "success"}
            onClick={onOpenTopRisks}
          />
          <MissionControlMetric
            label="Open alerts"
            value={metrics?.openAlerts || 0}
            supportingText="Open, acknowledged, or snoozed"
            tone={metrics?.openAlerts > 0 ? "danger" : "success"}
            onClick={onOpenAlerts}
          />
          <MissionControlMetric
            label="Stale Controls"
            value={metrics?.staleCount || 0}
            supportingText="Stale or never-observed rule evidence"
            tone={metrics?.staleCount > 0 ? "warning" : "success"}
            onClick={onOpenStaleControls}
          />
          <MissionControlMetric
            label="Accepted risk"
            value={metrics?.acceptedRiskFindings || 0}
            supportingText="Approved exceptions"
            tone={metrics?.acceptedRiskFindings > 0 ? "warning" : "success"}
            onClick={onOpenAcceptedRisk}
          />
          <MissionControlMetric
            label="Closed records"
            value={metrics?.closedTasks || 0}
            supportingText="Audit history preserved"
            tone={metrics?.closedTasks > 0 ? "success" : "neutral"}
            onClick={onOpenClosedRecords}
          />
        </div>
  );
}
